import { SB } from '@play-co/replicant';
import { getLocaleConfig } from 'src/replicant/creatives/text';
import { tuple } from 'src/replicant/utils/types';
import { getPetAssetKey, speciesChatbot } from './species';
import {
  createFlexMessageTemplate,
  createFlexContainer,
  createOpenGameAction,
} from './utils';
import { PetSpeciesID } from 'src/replicant/pet';
import { ChatbotMessagePayload } from '../messageTemplates';
import { buildSender } from '../sender';

/** Dream Drift resurrection artwork variants, independent of the active event theme. */
const dreamDriftResurrectionVariants = tuple(
  '2026-10-08-dreamdrift-heroes',
  '2026-10-08-dreamdrift-asia',
  '2026-10-08-dreamdrift-momotaro',
  '2026-10-08-dreamdrift-wonderland',
  '2026-10-08-dreamdrift-urashima',
);

export const resurrectionVariants = SB.tuple(
  tuple(
    '2024-12-13-puzzle_on_fire',
    '2024-12-13-house_on_fire',
    '2025-02-22-video_save_pet',
    '2025-02-22-video_save_pet_big',
    '2025-04-09-drowning_in_tears',
    '2025-04-09-owner_is_gone',
    '2025-04-09-crying_in_rain',
    '2025-04-09-bubble_bath',
    '2025-04-09-drowned_in_fluff',
    '2025-10-20-rescue-fire',
    '2025-10-20-rescue-drowning',
    '2025-11-19-hundred-gems',
    '2025-11-19-revive',
    '2025-11-19-house_on_fire_rescue',
    '2025-12-12-migration_tos_cryingburu',
    '2025-12-12-migration_tos_cryinggems',
    '2025-12-12-migration_tos_pleadingburu',
    '2025-12-12-migration_tos_pleadinggems',
    '2025-12-17-abducted',
    '2025-12-17-frozen',
    '2025-12-17-hell',
    '2025-12-17-tornado',
    '2025-12-17-vacuum',
    '2025-12-17-washingmachine_accident',
    '2026-01-08-bee_attack',
    '2026-01-08-cage',
    '2026-01-08-drills',
    '2026-01-08-drowning',
    '2026-01-08-lava',
    '2026-01-08-toxin',
    '2026-01-08-waterbox',
    '2026-04-28-50_gems_bear',
    '2026-04-28-50_gems_bear_capy',
    '2026-07-21-rescue_crocodile',
    '2026-07-17-drowning_rescue',
    '2026-07-22-frozen_rescue',
    '2026-07-29-league-lapsers-a',
    '2026-07-29-league-lapsers-b',
    '2026-10-13-stickers',
    '2026-10-13-stickers-apng-01',
    '2026-10-13-stickers-apng-02',
    '2026-10-13-stickers-apng-03',
    '2026-10-13-direct_sticker-static-01-start',
    '2026-10-13-direct_sticker-static-02-reminder',
    '2026-10-13-direct_sticker-static-03-ending',
    '2026-10-13-direct_sticker-apng-01',
    '2026-10-13-direct_sticker-apng-02',
    '2026-10-13-direct_sticker-apng-03',
    ...dreamDriftResurrectionVariants,
  ),
);

export type ResurrectionCreativeVariant = SB.ExtractType<
  typeof resurrectionVariants
>;

/**
 * Assets for message
 */
export const resurrectAssets: Record<
  Exclude<
    ResurrectionCreativeVariant,
    (typeof dreamDriftResurrectionVariants)[number]
  >,
  string
> = {
  '2024-12-13-puzzle_on_fire':
    'private/chatbot/resurrection/2024-12-13-puzzle_on_fire.png',
  '2024-12-13-house_on_fire':
    'private/chatbot/resurrection/2024-12-13-house_on_fire.png',
  '2025-02-22-video_save_pet':
    'private/chatbot/resurrection/2025-02-22-video_save_pet.mp4',
  '2025-02-22-video_save_pet_big':
    'private/chatbot/resurrection/2025-02-22-video_save_pet.mp4',
  '2025-04-09-drowning_in_tears':
    'private/chatbot/resurrection/2025-04-09-drowning_in_tears.png',
  '2025-04-09-owner_is_gone':
    'private/chatbot/resurrection/2025-04-09-owner_is_gone.png',
  '2025-04-09-crying_in_rain':
    'private/chatbot/resurrection/2025-04-09-crying_in_rain.png',
  '2025-04-09-bubble_bath':
    'private/chatbot/resurrection/2025-04-09-bubble_bath.png',
  '2025-04-09-drowned_in_fluff':
    'private/chatbot/resurrection/2025-04-09-drowned_in_fluff.png',
  '2025-10-20-rescue-fire':
    'private/chatbot/resurrection/2025-10-20-rescue-fire.png',
  '2025-10-20-rescue-drowning':
    'private/chatbot/resurrection/2025-10-20-rescue-drowning.png',
  '2025-11-19-hundred-gems':
    'private/chatbot/resurrection/2025-11-19-hundred-gems.png',
  '2025-11-19-revive': 'private/chatbot/resurrection/2025-11-19-revive.png',
  '2025-11-19-house_on_fire_rescue':
    'private/chatbot/resurrection/2024-12-13-house_on_fire.png',
  '2025-12-12-migration_tos_cryingburu':
    'private/chatbot/resurrection/2025-12-12-migration_tos_cryingburu.png',
  '2025-12-12-migration_tos_cryinggems':
    'private/chatbot/resurrection/2025-12-12-migration_tos_cryinggems.png',
  '2025-12-12-migration_tos_pleadingburu':
    'private/chatbot/resurrection/2025-12-12-migration_tos_pleadingburu.png',
  '2025-12-12-migration_tos_pleadinggems':
    'private/chatbot/resurrection/2025-12-12-migration_tos_pleadinggems.png',
  '2025-12-17-abducted': 'private/chatbot/resurrection/2025-12-17-abducted.jpg',
  '2025-12-17-frozen': 'private/chatbot/resurrection/2025-12-17-frozen.jpg',
  '2025-12-17-hell': 'private/chatbot/resurrection/2025-12-17-hell.jpg',
  '2025-12-17-tornado': 'private/chatbot/resurrection/2025-12-17-tornado.jpg',
  '2025-12-17-vacuum': 'private/chatbot/resurrection/2025-12-17-vacuum.jpg',
  '2025-12-17-washingmachine_accident':
    'private/chatbot/resurrection/2025-12-17-washingmachine_accident.jpg',
  '2026-01-08-bee_attack':
    'private/chatbot/resurrection/2026-01-08-bee_attack.jpg',
  '2026-01-08-cage': 'private/chatbot/resurrection/2026-01-08-cage.jpg',
  '2026-01-08-drills': 'private/chatbot/resurrection/2026-01-08-drills.jpg',
  '2026-01-08-drowning': 'private/chatbot/resurrection/2026-01-08-drowning.jpg',
  '2026-01-08-lava': 'private/chatbot/resurrection/2026-01-08-lava.jpg',
  '2026-01-08-toxin': 'private/chatbot/resurrection/2026-01-08-toxin.jpg',
  '2026-01-08-waterbox': 'private/chatbot/resurrection/2026-01-08-waterbox.jpg',
  '2026-04-28-50_gems_bear':
    'private/chatbot/resurrection/2026-04-28-50_gems_bear.png',
  '2026-04-28-50_gems_bear_capy':
    'private/chatbot/resurrection/2026-04-28-50_gems_bear_capy.png',
  '2026-07-21-rescue_crocodile':
    'private/chatbot/resurrection/2026-07-21-rescue_crocodile.png',
  '2026-07-17-drowning_rescue':
    'private/chatbot/resurrection/2026-01-08-drowning.jpg',
  '2026-07-22-frozen_rescue':
    'private/chatbot/resurrection/2026-07-22-frozen_rescue.png',
  '2026-07-29-league-lapsers-a':
    'private/chatbot/resurrection/2026-07-29-league-lapsers-a.png',
  '2026-07-29-league-lapsers-b':
    'private/chatbot/resurrection/2026-07-29-league-lapsers-b.png',
  '2026-10-13-stickers': 'private/chatbot/resurrection/2026-10-13-stickers.png',
  '2026-10-13-stickers-apng-01':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-01.png',
  '2026-10-13-stickers-apng-02':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-02.png',
  '2026-10-13-stickers-apng-03':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-03.png',
  '2026-10-13-direct_sticker-static-01-start':
    'private/chatbot/resurrection/2026-10-13-stickers-static-01-start.png',
  '2026-10-13-direct_sticker-static-02-reminder':
    'private/chatbot/resurrection/2026-10-13-stickers-static-02-reminder.png',
  '2026-10-13-direct_sticker-static-03-ending':
    'private/chatbot/resurrection/2026-10-13-stickers-static-03-ending.png',
  '2026-10-13-direct_sticker-apng-01':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-01.png',
  '2026-10-13-direct_sticker-apng-02':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-02.png',
  '2026-10-13-direct_sticker-apng-03':
    'private/chatbot/resurrection/2026-10-13-stickers-apng-03.png',
};

export const resurrectExtraAssets = {
  '2026-10-08-dreamdrift-heroes/en':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-heroes-en.png',
  '2026-10-08-dreamdrift-heroes/ja':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-heroes-ja.png',
  '2026-10-08-dreamdrift-asia/en':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-asia-en.png',
  '2026-10-08-dreamdrift-asia/ja':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-asia-ja.png',
  '2026-10-08-dreamdrift-momotaro/en':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-momotaro-en.png',
  '2026-10-08-dreamdrift-momotaro/ja':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-momotaro-ja.png',
  '2026-10-08-dreamdrift-wonderland/en':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-wonderland-en.png',
  '2026-10-08-dreamdrift-wonderland/ja':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-wonderland-ja.png',
  '2026-10-08-dreamdrift-urashima/en':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-urashima-en.png',
  '2026-10-08-dreamdrift-urashima/ja':
    'private/chatbot/resurrection/2026-10-08-dreamdrift-urashima-ja.png',

  '2025-02-22-video_save_pet_preview':
    'private/chatbot/resurrection/2025-02-22-video_save_pet.png',
  '2025-02-22-video_save_pet_big_preview':
    'private/chatbot/resurrection/2025-02-22-video_save_pet.png',
};

/**
 * Create resurrect message to return back users to the game, see:
 *
 * https://developers.line.biz/en/docs/messaging-api/flex-message-elements/
 * https://developers.line.biz/en/docs/messaging-api/using-flex-messages/
 *
 * Tools for generation of messages in flex format:
 * https://developers.line.biz/flex-simulator/
 */
export const resurrectMessage = createFlexMessageTemplate(
  SB.object({
    creativeAssetID: resurrectionVariants,
    species: SB.tuple(speciesChatbot),
    locale: SB.tuple(['en', 'ja'] as const),
    petName: SB.string(),
  }),
  (data, payload, api) => {
    const variant = data.creativeAssetID;
    const isDirectSticker = variant.startsWith('2026-10-13-direct_sticker-');
    // Sticker creatives use Japanese artwork and copy for every player locale.
    const locale =
      variant.startsWith('2026-10-13-stickers') || isDirectSticker
        ? 'ja'
        : data.locale;
    const altText = getLocaleConfig(locale).resurrect.altAll.text;
    const species: PetSpeciesID = data.species ?? 'ball';
    const normal = api.getAssetPath(getPetAssetKey(species, 'normal'));
    // Select localized Dream Drift artwork without binding the link to its theme.
    const isDreamDrift = dreamDriftResurrectionVariants.some(
      (creativeID) => creativeID === variant,
    );
    const background = api.getAssetPath(
      isDreamDrift ? `${variant}/${data.locale}` : variant,
    );

    switch (variant) {
      case '2025-02-22-video_save_pet':
      case '2025-02-22-video_save_pet_big': {
        const template = createVideoResurrectionMsgTemplate({
          altText: getLocaleConfig(data.locale).resurrect.playNow.text,
          // This is the correct type, but we don't get the type info here
          payload: payload as ChatbotMessagePayload,
          videoUrl: background,
          previewUrl: api.getAssetPath(variant + '_preview'),
          size: variant === '2025-02-22-video_save_pet' ? 'deca' : 'giga',
        });

        // Add sender to message
        template['sender'] = buildSender({
          name: data.petName,
          iconUrl: normal,
        });

        return template;
      }
      case '2025-11-19-revive': {
        const template = createReviveMsgTemplate({
          altText: getLocaleConfig(data.locale).resurrect.revive.text,
          // This is the correct type, but we don't get the type info here
          payload: payload as ChatbotMessagePayload,
          deadAssetUrl: api.getAssetPath(getPetAssetKey(species, 'dead')),
          happyAssetUrl: api.getAssetPath(getPetAssetKey(species, 'happy')),
          background,
        });

        // Add sender to message
        template['sender'] = buildSender({
          name: data.petName,
          iconUrl: normal,
        });

        return template;
      }
      default: {
        // The supplied sticker APNGs use landscape artwork and explicit playback.
        const isAnimatedSticker =
          variant.startsWith('2026-10-13-stickers-apng-') ||
          variant.startsWith('2026-10-13-direct_sticker-apng-');
        const template = createDefaultResurrectionMsgTemplate({
          altText,
          // Preserve the campaign attribution supplied by CSTool.
          payload: payload as ChatbotMessagePayload,
          background,
          ...(isDirectSticker
            ? {
                destinationURL:
                  'https://line.me/S/sticker/37826?utm_source=lad_oa_message',
              }
            : {}),
          ...(isAnimatedSticker ? { animated: true, aspectRatio: '3:2' } : {}),
        });

        // Add sender to message
        template['sender'] = buildSender({
          name: data.petName,
          iconUrl: normal,
        });

        return template;
      }
    }
  },
);

/**
 * Create a default resurrection message with static or animated artwork.
 * @param opts Message text, attribution, artwork presentation and optional destination.
 * @returns A Flex message that opens the destination URL or game when tapped.
 */
function createDefaultResurrectionMsgTemplate(opts: {
  altText: string;
  payload: ChatbotMessagePayload;
  background: string;
  /** Enable playback of the supplied APNG image. */
  animated?: boolean;
  /** Artwork proportions; omitted for square static creatives. */
  aspectRatio?: string;
  /** Open this URL directly instead of the game. */
  destinationURL?: string;
}) {
  const {
    altText,
    payload,
    background,
    animated,
    aspectRatio = '1:1',
    destinationURL,
  } = opts;

  return createFlexContainer(altText, {
    type: 'bubble',
    size: 'giga',
    action: destinationURL
      ? { type: 'uri', label: altText, uri: destinationURL }
      : createOpenGameAction(altText, payload),
    body: {
      type: 'box',
      layout: 'vertical',
      contents: [
        {
          type: 'image',
          url: background,
          size: 'full',
          aspectMode: 'cover',
          aspectRatio,
          ...(animated ? { animated: true } : {}),
          gravity: 'center',
        },
      ],
      paddingAll: '0px',
    },
  });
}

function createVideoResurrectionMsgTemplate(opts: {
  altText: string;
  payload: ChatbotMessagePayload;
  videoUrl: string;
  previewUrl: string;
  size: 'deca' | 'giga';
}) {
  const { altText, payload, videoUrl, previewUrl, size } = opts;

  return createFlexContainer(altText, {
    type: 'bubble',
    size,
    action: createOpenGameAction(altText, payload),
    hero: {
      type: 'video',
      url: videoUrl,
      previewUrl,
      altContent: {
        type: 'image',
        size: 'full',
        // NB, make sure to add different rations with different videos
        aspectRatio: '76:135',
        aspectMode: 'cover',
        url: previewUrl,
      },
      // NB, make sure to add different rations with different videos
      aspectRatio: '76:135',
      action: createOpenGameAction(altText, payload),
    },
    footer: {
      type: 'box',
      layout: 'vertical',
      spacing: 'sm',
      contents: [
        {
          type: 'box',
          layout: 'vertical',
          contents: [
            {
              type: 'text',
              text: altText,
              size: size === 'giga' ? 'xxl' : 'xl',
              align: 'center',
              weight: 'bold',
              color: '#FFFFFF',
            },
          ],
          cornerRadius: 'md',
          height: size === 'giga' ? '60px' : '50px',
          justifyContent: 'center',
          background: {
            type: 'linearGradient',
            angle: '0deg',
            startColor: '#28a745',
            endColor: '#19c951',
          },
        },
      ],
    },
  });
}

/**
 * Crate revive message template
 */
function createReviveMsgTemplate(opts: {
  altText: string;
  payload: ChatbotMessagePayload;
  background: string;
  deadAssetUrl: string;
  happyAssetUrl: string;
}) {
  const { payload, altText } = opts;

  const template = createFlexContainer(altText, {
    type: 'bubble',
    size: 'giga',
    action: createOpenGameAction(altText, payload),
    body: {
      type: 'box',
      layout: 'vertical',
      contents: [
        {
          type: 'image',
          url: opts.background,
          size: 'full',
          aspectMode: 'cover',
          aspectRatio: '1:1',
          gravity: 'center',
        },
        {
          type: 'box',
          layout: 'horizontal',
          position: 'absolute',
          width: '100%',
          height: '80%',
          offsetTop: '15%',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          contents: [
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'image',
                  url: opts.deadAssetUrl,
                  aspectMode: 'fit',
                  align: 'center',
                  gravity: 'center',
                },
              ],
              width: '50%',
              height: '80%',
              justifyContent: 'center',
              alignItems: 'center',
            },
            {
              type: 'box',
              layout: 'vertical',
              contents: [
                {
                  type: 'image',
                  url: opts.happyAssetUrl,
                  aspectMode: 'fit',
                  align: 'center',
                  gravity: 'center',
                },
              ],
              width: '50%',
              height: '80%',
              justifyContent: 'center',
              alignItems: 'center',
            },
          ],
        },
      ],
      paddingAll: '0px',
      justifyContent: 'center',
    },
  });

  return template;
}
